import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Printer, Clock, User } from "lucide-react";
import { format } from "date-fns";

interface OrderDetailItem {
  id: string;
  product_name: string;
  quantity: number;
  created_at: string;
  printed_at: string | null;
  source: string | null;
  created_by: string | null;
  operator_name: string | null;
}

interface OrderDetailsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string | null;
}

export function OrderDetailsPanel({ open, onOpenChange, orderId }: OrderDetailsPanelProps) {
  const [items, setItems] = useState<OrderDetailItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !orderId) return;

    const fetchDetails = async () => {
      setLoading(true);
      try {
        const { data: orderItems, error } = await supabase
          .from("order_items")
          .select("id, product_name, quantity, created_at, printed_at, source, created_by")
          .eq("order_id", orderId)
          .order("created_at", { ascending: true });

        if (error) throw error;

        // Fetch operator names for items with created_by
        const userIds = [...new Set((orderItems || []).map(i => i.created_by).filter(Boolean))] as string[];
        
        let profilesMap: Record<string, string> = {};
        if (userIds.length > 0) {
          const { data: profiles } = await supabase
            .from("profiles")
            .select("id, full_name")
            .in("id", userIds);
          
          if (profiles) {
            profilesMap = Object.fromEntries(profiles.map(p => [p.id, p.full_name || "Sem nome"]));
          }
        }

        setItems(
          (orderItems || []).map(item => ({
            ...item,
            operator_name: item.source === "public"
              ? "Cliente"
              : item.created_by
                ? profilesMap[item.created_by] || "Operador"
                : null,
          }))
        );
      } catch (err) {
        console.error("Erro ao buscar detalhes:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [open, orderId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--pdv-surface-panel,#0b1020)] border-[var(--pdv-border,#1e2d52)] text-[var(--pdv-text,#c8dff5)] max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-[var(--pdv-text,#c8dff5)] text-base">
            Detalhes do Pedido
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 -mx-6 px-6">
          {loading ? (
            <p className="text-sm text-[var(--pdv-text-subtle,#4a7abf)] py-4 text-center">Carregando...</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-[var(--pdv-text-subtle,#4a7abf)] py-4 text-center">Nenhum item encontrado</p>
          ) : (
            <div className="space-y-1.5">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-start gap-3 py-2 px-2.5 rounded-lg bg-[var(--pdv-surface,#111829)] border border-[var(--pdv-border,#1e2d52)]"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-[var(--pdv-text,#c8dff5)] truncate">
                        {item.quantity}x {item.product_name}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-[var(--pdv-text-subtle,#4a7abf)]">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {format(new Date(item.created_at), "HH:mm:ss")}
                      </span>
                      {item.operator_name && (
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {item.operator_name}
                        </span>
                      )}
                      {item.printed_at && (
                        <span className="flex items-center gap-1 text-[var(--pdv-success,#00e676)]">
                          <Printer className="h-3 w-3" />
                          {format(new Date(item.printed_at), "HH:mm:ss")}
                        </span>
                      )}
                      {!item.printed_at && (
                        <span className="text-orange-400 text-[10px]">Não impresso</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
